/**
 * Landing Page
 * 公共區域，不需要 tenant 和 auth
 * 傳遞品牌自主權與私域流量核心價值
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ShoppingCart,
  Store,
  MessageCircle,
  Bot,
  ArrowRight,
  Zap,
  LineChart,
} from 'lucide-react';

// Custom hook for scroll animation
function useIntersectionObserver(options = {}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsIntersecting(true);
        if (targetRef.current) {
          observer.unobserve(targetRef.current);
        }
      }
    }, options);

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [options]);

  return [targetRef, isIntersecting] as const;
}

function FadeInSection({
  children,
  delay = 0,
}: {
  children: ReactNode;
  delay?: number;
}) {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
  });

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function App() {
  useEffect(() => {
    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    fetch(`${apiBaseUrl}/health`).catch(() => {});
  }, []);

  const handleLogin = () => {
    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    const redirectUri = `${window.location.origin}/tenant-register`;
    window.location.href = `${apiBaseUrl}/api/auth/google?redirect_uri=${encodeURIComponent(
      redirectUri
    )}`;
  };

  const handleRegisterTenant = () => {
    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    const redirectUri = `${window.location.origin}/tenant-register?action=create`;
    window.location.href = `${apiBaseUrl}/api/auth/google?redirect_uri=${encodeURIComponent(
      redirectUri
    )}`;
  };

  return (
    <div className="min-h-screen bg-[#0f1115] text-slate-200 font-sans selection:bg-blue-500/30 overflow-hidden relative">
      {/* Background Tech Geometric Fluid Animation effect (simulated with CSS gradients) */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-600/10 blur-[120px] mix-blend-screen animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-orange-600/10 blur-[120px] mix-blend-screen animate-pulse delay-1000" />
      </div>

      {/* Persistent Mascot */}
      <div className="fixed bottom-8 right-8 z-50 animate-bounce cursor-pointer group">
        <div className="bg-slate-800 border border-slate-700 p-3 rounded-full shadow-2xl shadow-blue-500/20 group-hover:scale-110 transition-transform">
          <Bot className="w-8 h-8 text-blue-400 group-hover:text-orange-400 transition-colors" />
        </div>
      </div>

      <div className="relative z-10">
        {/* Navigation */}
        <nav className="px-6 py-6 flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold tracking-wider text-white">
              EMVYAX
            </span>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleLogin}
              className="px-5 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Log in
            </button>
            <button
              onClick={handleRegisterTenant}
              className="px-5 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
            >
              Get Started
            </button>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="px-6 pt-24 pb-32 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16">
          <div className="flex-1 text-center lg:text-left">
            <FadeInSection>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-8 tracking-wide">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                SYSTEM ONLINE
              </div>
              <h1 className="text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
                YOUR 24/7
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-500 to-orange-400">
                  MICRO-SECRETARY.
                </span>
              </h1>
              <p className="text-xl lg:text-2xl text-slate-400 mb-4 font-light tracking-wide">
                BUILD YOUR BUSINESS, NOT YOUR PAPERWORK.
              </p>
              <p className="text-lg text-slate-500 mb-10 max-w-xl mx-auto lg:mx-0">
                AUTOMATE 100+ TASKS IN LINE. Say goodbye to high platform fees
                and manual data entry. Reclaim your brand and customer data
                today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={handleRegisterTenant}
                  className="px-8 py-4 bg-white text-[#0f1115] hover:bg-slate-200 text-lg font-bold rounded-xl flex items-center justify-center gap-2 transition-all transform hover:-translate-y-1"
                >
                  Start Automating
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => (window.location.href = '/invite/accept')}
                  className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white text-lg font-bold rounded-xl border border-slate-700 transition-all"
                >
                  Have an Invite?
                </button>
              </div>
            </FadeInSection>
          </div>

          <div className="flex-1 relative w-full max-w-lg lg:max-w-none">
            <FadeInSection delay={200}>
              <div className="relative h-[500px] w-full flex items-center justify-center">
                {/* Tablet Mockup (Dashboard) */}
                <div className="absolute right-0 top-0 w-[80%] h-[80%] bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl shadow-black overflow-hidden flex flex-col transform rotate-2 hover:rotate-0 transition-transform duration-500 z-10">
                  <div className="h-8 bg-slate-900 border-b border-slate-700 flex items-center px-4 gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 p-6 flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                      <div className="h-6 w-32 bg-slate-700 rounded-md" />
                      <div className="h-8 w-8 bg-blue-500/20 rounded-md" />
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1 h-24 bg-slate-700/50 rounded-xl border border-slate-600/50" />
                      <div className="flex-1 h-24 bg-slate-700/50 rounded-xl border border-slate-600/50" />
                      <div className="flex-1 h-24 bg-slate-700/50 rounded-xl border border-slate-600/50" />
                    </div>
                    <div className="flex-1 bg-slate-700/30 rounded-xl border border-slate-600/30 flex items-end p-4 gap-2">
                      {[40, 70, 45, 90, 65, 80, 55].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-blue-500/50 rounded-t-sm transition-all duration-1000 animate-pulse"
                          style={{
                            height: `${h}%`,
                            animationDelay: `${i * 100}ms`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Phone Mockup (LINE) */}
                <div className="absolute left-0 bottom-0 w-[45%] h-[90%] bg-slate-900 rounded-[2rem] border-4 border-slate-700 shadow-2xl shadow-black overflow-hidden flex flex-col transform -rotate-6 hover:rotate-0 transition-transform duration-500 z-20">
                  <div className="h-14 bg-[#06C755] flex items-end justify-center pb-3">
                    <span className="text-white font-bold text-sm tracking-wide">
                      LINE OA
                    </span>
                  </div>
                  <div className="flex-1 bg-[#7490C2] p-4 flex flex-col gap-3 overflow-hidden relative">
                    <div className="self-end bg-[#85E249] text-black px-4 py-2 rounded-2xl rounded-tr-sm text-xs max-w-[80%] shadow-sm">
                      Order: 2x Iced Latte
                    </div>
                    <div className="self-start bg-white text-black px-4 py-2 rounded-2xl rounded-tl-sm text-xs max-w-[80%] shadow-sm">
                      Confirmed! Pickup in 10 mins.
                    </div>
                    <div className="self-end bg-[#85E249] text-black px-4 py-2 rounded-2xl rounded-tr-sm text-xs max-w-[80%] shadow-sm mt-4">
                      Check my loyalty points?
                    </div>
                    <div className="self-start bg-white text-black px-4 py-2 rounded-2xl rounded-tl-sm text-xs max-w-[80%] shadow-sm">
                      You have 450 points! 🎉
                    </div>
                    {/* Floating elements */}
                    <div className="absolute bottom-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg animate-bounce">
                      <Bot className="w-6 h-6 text-blue-500" />
                    </div>
                  </div>
                </div>

                {/* Connection Line */}
                <div className="absolute top-1/2 left-[30%] right-[30%] h-0.5 bg-gradient-to-r from-[#06C755] to-blue-500 z-15 transform -rotate-12 opacity-50">
                  <div className="w-2 h-2 rounded-full bg-white absolute top-1/2 -translate-y-1/2 animate-ping shadow-[0_0_10px_#fff]" />
                </div>
              </div>
            </FadeInSection>
          </div>
        </section>

        {/* Feature Blocks */}
        <section className="px-6 py-32 bg-slate-900/50 border-y border-slate-800/50">
          <div className="max-w-7xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-24">
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
                  INTEGRATED ECOSYSTEM
                </h2>
                <p className="text-xl text-slate-400 font-light max-w-2xl mx-auto">
                  Everything you need to run your business, unified in one clean
                  interface.
                </p>
              </div>
            </FadeInSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              <FadeInSection delay={100}>
                <div className="group bg-slate-800/40 border border-slate-700/50 rounded-3xl p-10 hover:bg-slate-800/80 transition-colors h-full">
                  <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                    <LineChart className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Smart CRM & Analytics
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    Transform one-time buyers into loyal advocates. Track
                    customer preferences, automate targeted campaigns, and
                    visualize your growth with real-time, actionable insights.
                  </p>
                </div>
              </FadeInSection>

              <FadeInSection delay={200}>
                <div className="group bg-slate-800/40 border border-slate-700/50 rounded-3xl p-10 hover:bg-slate-800/80 transition-colors h-full">
                  <div className="w-16 h-16 bg-orange-500/10 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                    <ShoppingCart className="w-8 h-8 text-orange-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Seamless POS Integration
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    Zero transaction fees on your owned channels. A
                    lightning-fast point of sale system that syncs perfectly
                    with your online store and LINE OA in real-time.
                  </p>
                </div>
              </FadeInSection>

              <FadeInSection delay={300}>
                <div className="group bg-slate-800/40 border border-slate-700/50 rounded-3xl p-10 hover:bg-slate-800/80 transition-colors h-full">
                  <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                    <MessageCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    LINE Official Account Power-up
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    Turn chats into checkouts. Allow customers to browse, order,
                    and pay directly within LINE. Automated notifications keep
                    them engaged without manual effort.
                  </p>
                </div>
              </FadeInSection>

              <FadeInSection delay={400}>
                <div className="group bg-slate-800/40 border border-slate-700/50 rounded-3xl p-10 hover:bg-slate-800/80 transition-colors h-full">
                  <div className="w-16 h-16 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                    <Store className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Instant Cloud Storefront
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    Deploy your branded digital storefront in minutes. No coding
                    required. fully responsive, SEO-optimized, and built to
                    convert traffic into sales.
                  </p>
                </div>
              </FadeInSection>
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="px-6 py-32">
          <div className="max-w-4xl mx-auto text-center">
            <FadeInSection>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-10">
                WHY CHOOSE EMVYAX?
              </h2>
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 lg:p-12 text-left relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-orange-500" />
                <blockquote className="text-2xl text-white font-medium italic mb-10">
                  "Stop letting high platform fees eat your margins. Own your
                  digital presence."
                </blockquote>
                <ul className="space-y-6">
                  <li className="flex gap-4 items-start">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold">
                      1
                    </span>
                    <p className="text-lg text-slate-300">
                      <strong className="text-white">
                        Zero Commission Traps
                      </strong>{' '}
                      — Don't work for the platforms. Keep what you earn with
                      your own direct channels.
                    </p>
                  </li>
                  <li className="flex gap-4 items-start">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold">
                      2
                    </span>
                    <p className="text-lg text-slate-300">
                      <strong className="text-white">Own Your Data</strong> —
                      Know your customers. Build direct relationships instead of
                      renting access to them.
                    </p>
                  </li>
                  <li className="flex gap-4 items-start">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center font-bold">
                      3
                    </span>
                    <p className="text-lg text-slate-300">
                      <strong className="text-white">
                        Private Traffic Mastery
                      </strong>{' '}
                      — Lower acquisition costs and drive immense loyalty
                      through your owned ecosystem.
                    </p>
                  </li>
                </ul>
              </div>
            </FadeInSection>
          </div>
        </section>

        {/* Footer CTA */}
        <footer className="px-6 py-20 border-t border-slate-800/50 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/10 to-transparent pointer-events-none" />
          <FadeInSection>
            <div className="max-w-3xl mx-auto relative z-10">
              <h2 className="text-4xl font-bold text-white mb-8">
                Ready to reclaim your business?
              </h2>
              <button
                onClick={handleRegisterTenant}
                className="px-10 py-5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-xl font-bold rounded-2xl shadow-xl shadow-blue-500/20 hover:shadow-blue-500/40 transition-all transform hover:-translate-y-1 mb-12 inline-block"
              >
                Create Your Account Now
              </button>
              <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-slate-800 text-slate-500 text-sm gap-4">
                <p>
                  © {new Date().getFullYear()} Emvyax. All rights reserved.
                </p>
                <div className="flex gap-6">
                  <button
                    onClick={handleLogin}
                    className="hover:text-white transition-colors"
                  >
                    Log in
                  </button>
                  <a
                    href="/invite/accept"
                    className="hover:text-white transition-colors"
                  >
                    Accept Invite
                  </a>
                </div>
              </div>
            </div>
          </FadeInSection>
        </footer>
      </div>
    </div>
  );
}

export default App;
